import { useHotkeys } from 'react-hotkeys-hook';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import { uiStore } from 'stores';

export default function HotkeyInfoComponent() {
  const { hotkeyInfoOpen } = useSnapshot(uiStore.state);

  useHotkeys(
    `shift+h`,
    () => {
      uiStore.toggleHotkeyInfoOpen();
    },
    {
      preventDefault: true,
    },
  );

  if (!hotkeyInfoOpen) return null;

  return <HotkeyInfo close={uiStore.toggleHotkeyInfoOpen} />;
}

function HotkeyInfo({ close }: { close: () => void }) {
  useHotkeys('escape', () => close(), {
    preventDefault: true,
  });
  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-stretch justify-center sm:items-center">
      <div
        className="pointer-events-auto absolute inset-0 z-0"
        onClick={close}
      />
      <div className="bg-gray-90/90 pointer-events-auto z-10 w-full p-4 text-white sm:w-auto sm:min-w-[400px] sm:rounded-2xl">
        <div className="border-gray-60 mb-4 flex flex-col border-b sm:mb-0 sm:border-b-0">
          <Scope name="Player">
            <Hotkeys
              hotkeys={[
                { name: 'Play/Pause', keys: ['Space'] },
                { name: 'Jump frame', keys: ['← / →'] },
                { name: 'Jump 10 frames', keys: ['Shift + ← / →'] },
              ]}
            />
          </Scope>
          <Scope name="Camera">
            <Hotkeys hotkeys={[{ name: 'Toggle move', keys: ['Shift'] }]} />
            <Hotkeys hotkeys={[{ name: 'Zoom in', keys: ['Shift + plus'] }]} />
            <Hotkeys
              hotkeys={[{ name: 'Zoom out', keys: ['Shift + minus'] }]}
            />
          </Scope>
          <Scope name="Scene">
            <Hotkeys
              hotkeys={[
                { name: 'Toggle grid', keys: ['Shift + g'] },
                { name: 'Toggle skeleton', keys: ['Shift + m'] },
              ]}
            />
          </Scope>
          <Scope name="UI">
            <Hotkeys hotkeys={[{ name: 'Close modals', keys: ['Escape'] }]} />
            <Hotkeys
              hotkeys={[{ name: 'Toggle dark mode', keys: ['Shift + l'] }]}
            />
            <Hotkeys
              hotkeys={[{ name: 'Toggle hotkeys', keys: ['Shift + h'] }]}
            />
          </Scope>
        </div>
        <Button onClick={close} className="mt-auto sm:hidden">
          Close
        </Button>
      </div>
    </div>
  );
}

const Scope = ({
  children,
  name,
}: {
  children: React.ReactNode;
  name: string;
}) => {
  return (
    <div className="border-gray-60 relative mb-4 flex flex-col gap-3 border-b pb-4 last:border-b-0">
      <Text as="h2" variant="studio/body/medium/md">
        {name}
      </Text>
      {children}
    </div>
  );
};

const Hotkeys = ({
  hotkeys,
}: {
  hotkeys: {
    name: string;
    keys: string[];
  }[];
}) => {
  return (
    <dl className="grid grid-cols-[120px_1fr] items-center justify-items-start gap-x-4 gap-y-4">
      {hotkeys.map((hotkey) => (
        <>
          <Text as="dt" variant="studio/body/md" className="text-gray-45">
            {hotkey.name}
          </Text>
          <Text as="dd" variant="studio/body/md" className="flex gap-1.5">
            {hotkey.keys.map((key) => (
              <>
                {key.split(' + ').map((k, i, arr) => (
                  <>
                    {k.split(' / ').map((s, si, sArr) => (
                      <>
                        <kbd key={s + i + si}>{s}</kbd>
                        {si < sArr.length - 1 && <span>/</span>}
                      </>
                    ))}
                    {i < arr.length - 1 && <span>+</span>}
                  </>
                ))}
                {hotkey.keys.length > 1 && <span>,</span>}
              </>
            ))}
          </Text>
        </>
      ))}
    </dl>
  );
};
